import isSafariBackgroundRequestFail from '@/utils/platform/isSafariBackgroundRequestFail';

import pollRequest, { PollStatus } from './pollRequest';
import { type PollResponse } from './pollRequest';

type PollUntilCompletedOptions = {
  url: string;
  initialInterval?: number;
  maxInterval?: number;
  backoffFactor?: number;
  maxAttempts?: number;
  maxRestarts?: number;
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const pollUntilCompleted = async ({
  url,
  initialInterval = 1000,
  maxInterval = 8000,
  backoffFactor = 1.5,
  maxAttempts = 150,
  maxRestarts = 3,
}: PollUntilCompletedOptions) => {
  let restarts = 0;

  const runPolling = async (): Promise<PollResponse> => {
    let pollingResult: PollResponse | undefined;
    let attempts = 0;
    let currentInterval = initialInterval;

    do {
      // eslint-disable-next-line no-await-in-loop
      await sleep(currentInterval);
      try {
        // eslint-disable-next-line no-await-in-loop
        pollingResult = await pollRequest(url);
      } catch (error) {
        if (isSafariBackgroundRequestFail(error) && restarts < maxRestarts) {
          restarts++;
          return runPolling();
        }
        throw error;
      }
      attempts++;
      currentInterval = Math.min(currentInterval * backoffFactor, maxInterval);
    } while (
      attempts < maxAttempts &&
      pollingResult.response.status !== PollStatus.Completed &&
      pollingResult.response.status !== PollStatus.Failed
    );

    if (pollingResult.response.status === PollStatus.Failed) {
      throw new Error(
        `Workflow execution failed: ${JSON.stringify(pollingResult.response)}`
      );
    }

    if (
      attempts >= maxAttempts &&
      pollingResult.response.status !== PollStatus.Completed
    ) {
      throw new Error('Polling timeout: Maximum attempts reached');
    }

    return pollingResult;
  };

  return runPolling();
};

export default pollUntilCompleted;
